import { RPACourse } from '../types';

export const POWER_AUTOMATE_COURSES: RPACourse[] = [
  {
    id: 'power-automate-desktop-fundamentals',
    title: 'Power Automate Desktop Fundamentals',
    description: 'Master Microsoft Power Automate Desktop for Windows automation, from basic workflows to complex business processes.',
    level: 'beginner',
    duration: '10 hours',
    category: 'power-automate',
    platform: 'Power Automate Desktop',
    certificationPrep: true,
    prerequisites: [],
    learningObjectives: [
      'Navigate Power Automate Desktop interface and create basic flows',
      'Automate Windows applications and file operations',
      'Implement web automation and data extraction',
      'Handle variables, loops, and conditional logic',
      'Apply error handling and debugging techniques'
    ],
    tags: ['Power Automate', 'Desktop Automation', 'Microsoft', 'Windows', 'RPA'],
    modules: [
      {
        id: 'pad-introduction',
        title: 'Introduction to Power Automate Desktop',
        description: 'Get started with Power Automate Desktop and understand its capabilities',
        content: `# Introduction to Power Automate Desktop

## What is Power Automate Desktop?

Power Automate Desktop is Microsoft's robotic process automation (RPA) solution that enables you to automate repetitive tasks on your Windows desktop. It's part of the broader Microsoft Power Platform ecosystem, providing seamless integration with cloud services and other Microsoft applications.

## Key Features and Capabilities

### Desktop Automation
- **Application Integration**: Automate any Windows application, from legacy systems to modern web applications
- **File Operations**: Comprehensive file and folder manipulation capabilities
- **System Commands**: Execute system-level operations and commands
- **Screen Automation**: Visual-based automation using image recognition

### Web Automation
- **Browser Support**: Works with all major browsers (Chrome, Edge, Firefox, Internet Explorer)
- **Web Element Interaction**: Click buttons, fill forms, and extract data from web pages
- **JavaScript Execution**: Run custom JavaScript code within web pages
- **File Downloads**: Automate file download and processing workflows

### Data Processing
- **Excel Integration**: Native Excel operations without requiring Excel installation
- **Database Connectivity**: Connect to various database systems
- **API Integration**: Consume REST APIs and web services
- **Data Transformation**: Built-in data manipulation and transformation capabilities

## Installation and Setup

### System Requirements
- **Operating System**: Windows 10 (version 1909 or later) or Windows 11
- **Memory**: Minimum 2 GB RAM (4 GB recommended)
- **Storage**: At least 1 GB available disk space
- **Browser**: Microsoft Edge WebView2 (automatically installed if needed)

### Installation Process
1. **Download**: Get Power Automate Desktop from the Microsoft website or Microsoft Store
2. **Install**: Run the installer with administrator privileges
3. **Sign In**: Use your Microsoft account or organizational account
4. **License**: Obtain appropriate license (included with Windows 11 and certain Microsoft 365 plans)

### Initial Configuration
\`\`\`
First Launch Setup:
1. Accept terms and conditions
2. Choose installation directory
3. Configure browser extensions (recommended)
4. Set up proxy settings if required
5. Configure logging and error reporting preferences
\`\`\`

## Interface Navigation

### Main Console
The Power Automate Desktop console is your central hub for managing flows and accessing resources.

#### Navigation Panel
- **My flows**: Your personal automation flows
- **Shared with me**: Flows shared by other users
- **Examples**: Pre-built example flows for learning
- **Templates**: Ready-to-use templates for common scenarios

#### Flow Management
- **Create new flow**: Start building automation from scratch
- **Import flow**: Import flows from files or other sources
- **Export flow**: Share or backup your flows
- **Run flow**: Execute flows immediately or on schedule

### Flow Designer
The visual designer where you build your automation workflows.

#### Designer Components
- **Actions Pane**: Library of available automation actions organized by category
- **Main Pane**: Visual workflow canvas where you drag and drop actions
- **Variables Pane**: Manage flow variables and their values
- **UI Elements Pane**: Store and manage captured UI elements

#### Action Categories
- **System**: Basic system operations and file management
- **Variables**: Data manipulation and variable operations
- **Conditionals**: If-else logic and decision making
- **Loops**: Repetitive operations and iterations
- **Web browser**: Web automation and browser control
- **Excel**: Spreadsheet operations and data processing
- **Email**: Email sending and processing
- **Database**: Database connectivity and operations

## Basic Concepts

### Actions
Actions are the building blocks of Power Automate Desktop flows. Each action performs a specific task.

#### Action Structure
\`\`\`
Action Components:
- Name: Descriptive identifier for the action
- Parameters: Input values that configure the action
- Variables: Output values generated by the action
- Error Handling: How to handle errors for this action
\`\`\`

#### Common Action Types
- **Input Actions**: Send keystrokes, mouse clicks, and text input
- **Output Actions**: Capture text, images, and data from applications
- **Control Actions**: Manage flow execution and decision making
- **Data Actions**: Process, transform, and manipulate data

### Variables
Variables store data that can be used throughout your flow.

#### Variable Types
- **Text**: String values for names, messages, and textual data
- **Number**: Numeric values for calculations and counters
- **Boolean**: True/false values for logical operations
- **DateTime**: Date and time values for scheduling and timestamps
- **List**: Collections of items for batch processing
- **DataTable**: Structured data similar to spreadsheet tables
- **Custom Object**: Complex data structures for advanced scenarios

#### Variable Scope
- **Local Variables**: Available only within the current subflow
- **Input Variables**: Receive data from calling flows or external sources
- **Output Variables**: Return data to calling flows or external systems

### Flow Structure
Flows organize actions in a logical sequence to accomplish business objectives.

#### Linear Flows
Sequential execution of actions from start to finish:
\`\`\`
Start → Action 1 → Action 2 → Action 3 → End
\`\`\`

#### Conditional Flows
Branching logic based on conditions:
\`\`\`
Start → Condition → [True Path / False Path] → End
\`\`\`

#### Loop Flows
Repetitive execution for batch processing:
\`\`\`
Start → Loop Start → Actions → Loop End → End
\`\`\`

## Error Handling
Robust error handling ensures your flows continue running even when unexpected issues occur.

### Error Types
- **Action Errors**: Specific action failures (element not found, file locked, etc.)
- **System Errors**: System-level issues (out of memory, network problems, etc.)
- **Logic Errors**: Incorrect flow logic or data validation failures

### Error Handling Strategies
- **Continue on Error**: Skip failed actions and continue with the next action
- **Retry**: Attempt the failed action multiple times with delays
- **Alternative Path**: Execute different actions when primary actions fail
- **Terminate**: Stop the flow execution and report the error

## Best Practices

### Flow Design
- **Start Simple**: Begin with basic flows and gradually add complexity
- **Modular Design**: Break complex processes into smaller, reusable subflows
- **Clear Naming**: Use descriptive names for flows, actions, and variables
- **Documentation**: Add comments and descriptions to explain complex logic

### Performance Optimization
- **Minimize Waits**: Use appropriate wait strategies without excessive delays
- **Efficient Selectors**: Use reliable UI element identification methods
- **Resource Management**: Properly close applications and free resources
- **Error Prevention**: Validate inputs and handle edge cases proactively

### Security Considerations
- **Credential Management**: Use secure methods for storing and accessing credentials
- **Data Protection**: Implement appropriate data handling and privacy measures
- **Access Control**: Limit flow access and modification rights appropriately
- **Audit Trail**: Maintain logs for compliance and troubleshooting

## Common Use Cases

### Business Process Automation
- **Data Entry**: Automate repetitive data input tasks
- **Report Generation**: Create and distribute regular reports
- **File Processing**: Organize, transform, and distribute files
- **System Integration**: Connect different applications and systems

### Personal Productivity
- **Email Management**: Organize and respond to emails automatically
- **Calendar Management**: Schedule meetings and appointments
- **File Organization**: Sort and organize personal files
- **Web Research**: Gather information from multiple sources

## Getting Started Tips

### Learning Path
1. **Explore Examples**: Study pre-built example flows to understand patterns
2. **Start Small**: Create simple flows for familiar tasks
3. **Practice Regularly**: Build flows for different scenarios to gain experience
4. **Join Community**: Participate in Power Automate communities and forums

### Development Environment
- **Test Environment**: Use a dedicated environment for flow development and testing
- **Version Control**: Maintain backups and versions of important flows
- **Documentation**: Keep detailed documentation of flow purposes and logic
- **Monitoring**: Regularly review flow performance and error logs

## Key Takeaways

Power Automate Desktop provides a powerful, accessible platform for Windows automation. Understanding its interface, basic concepts, and best practices is essential for successful automation implementation. Focus on learning the action library, variable management, and error handling to build robust, reliable flows. Start with simple scenarios and gradually increase complexity as you gain experience with the platform.`,
        duration: '1.5 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Install Power Automate Desktop',
          'Create your first simple automation',
          'Explore the action library'
        ]
      },
      {
        id: 'desktop-automation',
        title: 'Windows Desktop Application Automation',
        description: 'Automate Windows applications, mouse and keyboard actions',
        content: 'Desktop automation techniques, application interaction, and UI element manipulation.',
        duration: '2.5 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Automate calculator operations',
          'Extract data from desktop applications',
          'Automate file management tasks'
        ]
      },
      {
        id: 'web-automation-pad',
        title: 'Web Automation with Power Automate Desktop',
        description: 'Master web browser automation and data extraction',
        content: 'Web automation strategies, browser interaction, and web data extraction techniques.',
        duration: '2 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Automate web form submissions',
          'Extract data from web tables',
          'Navigate multi-page web workflows'
        ]
      },
      {
        id: 'variables-control-flow',
        title: 'Variables, Lists, and Control Flow',
        description: 'Work with data structures and implement complex logic',
        content: 'Data manipulation, list operations, conditional logic, and loop structures.',
        duration: '2 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Process lists of data',
          'Implement conditional workflows',
          'Create dynamic automation logic'
        ]
      },
      {
        id: 'file-email-operations',
        title: 'File Operations and Email Automation',
        description: 'Automate file processing and email communications',
        content: 'File system operations, Excel automation, and email processing capabilities.',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Process multiple Excel files',
          'Automate email responses',
          'Organize files based on content'
        ]
      },
      {
        id: 'debugging-error-handling',
        title: 'Debugging and Error Handling',
        description: 'Master debugging techniques and implement robust error handling',
        content: 'Debugging tools, error handling strategies, and flow optimization techniques.',
        duration: '0.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Debug complex automation flows',
          'Implement try-catch error handling',
          'Create self-healing automations'
        ]
      }
    ]
  },
  {
    id: 'power-automate-cloud-flows',
    title: 'Power Automate Cloud Flows & Connectors',
    description: 'Build cloud-based automation workflows with Power Automate, integrating various services and applications.',
    level: 'intermediate',
    duration: '12 hours',
    category: 'power-automate',
    platform: 'Power Automate (Cloud)',
    certificationPrep: true,
    prerequisites: ['power-automate-desktop-fundamentals'],
    learningObjectives: [
      'Design and build cloud-based automation flows',
      'Integrate multiple services using connectors',
      'Implement approval workflows and business processes',
      'Handle data transformation and API integrations',
      'Monitor and manage cloud automation solutions'
    ],
    tags: ['Power Automate', 'Cloud Flows', 'Connectors', 'Integration', 'Workflow'],
    modules: [
      {
        id: 'cloud-flows-basics',
        title: 'Cloud Flows Fundamentals',
        description: 'Master the basics of Power Automate cloud flows and triggers',
        content: 'Cloud flow architecture, triggers, actions, and basic workflow design principles.',
        duration: '2 hours',
        difficulty: 'beginner'
      },
      {
        id: 'connectors-integration',
        title: 'Connectors and Service Integration',
        description: 'Connect and integrate various cloud services and applications',
        content: 'Connector ecosystem, service authentication, and cross-platform integration strategies.',
        duration: '3 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'approval-workflows',
        title: 'Approval Workflows and Business Processes',
        description: 'Build sophisticated approval workflows and business process automation',
        content: 'Approval flow design, parallel approvals, and complex business process implementation.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'data-transformation',
        title: 'Data Operations and Transformation',
        description: 'Master data manipulation, transformation, and processing in cloud flows',
        content: 'Data operations, expressions, functions, and advanced data transformation techniques.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'api-custom-connectors',
        title: 'Custom Connectors and API Integration',
        description: 'Create custom connectors and integrate with external APIs',
        content: 'Custom connector development, API integration, and authentication handling.',
        duration: '1.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'monitoring-governance',
        title: 'Flow Monitoring and Governance',
        description: 'Monitor performance, manage errors, and establish governance policies',
        content: 'Flow monitoring, error handling, performance optimization, and governance frameworks.',
        duration: '0.5 hours',
        difficulty: 'intermediate'
      }
    ]
  },
  {
    id: 'power-platform-integration',
    title: 'Advanced Power Platform Integration',
    description: 'Integrate Power Automate with Power Apps, Power BI, and other Microsoft 365 services for comprehensive solutions.',
    level: 'advanced',
    duration: '14 hours',
    category: 'power-automate',
    platform: 'Power Platform',
    certificationPrep: true,
    prerequisites: ['power-automate-cloud-flows'],
    learningObjectives: [
      'Integrate Power Automate with Power Apps and Power BI',
      'Build comprehensive Power Platform solutions',
      'Implement advanced SharePoint and Teams automation',
      'Create custom business applications with automation',
      'Establish enterprise-grade Power Platform governance'
    ],
    tags: ['Power Platform', 'Power Apps', 'Power BI', 'SharePoint', 'Teams', 'Integration'],
    modules: [
      {
        id: 'power-apps-integration',
        title: 'Power Apps and Power Automate Integration',
        description: 'Build integrated solutions combining Power Apps and Power Automate',
        content: 'Power Apps integration, canvas app automation, and model-driven app workflows.',
        duration: '3.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'power-bi-automation',
        title: 'Power BI Data Refresh and Report Automation',
        description: 'Automate Power BI operations, data refresh, and report distribution',
        content: 'Power BI automation, dataset management, and automated reporting solutions.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'sharepoint-teams-automation',
        title: 'Advanced SharePoint and Teams Automation',
        description: 'Implement sophisticated SharePoint and Teams automation workflows',
        content: 'SharePoint automation, Teams integration, and collaborative workflow solutions.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'dataverse-integration',
        title: 'Dataverse and Common Data Model Integration',
        description: 'Leverage Dataverse for enterprise data management and automation',
        content: 'Dataverse integration, data modeling, and enterprise data automation strategies.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'enterprise-solutions',
        title: 'Enterprise Solution Architecture',
        description: 'Design and implement enterprise-grade Power Platform solutions',
        content: 'Solution architecture, environment management, and enterprise deployment strategies.',
        duration: '2 hours',
        difficulty: 'advanced'
      },
      {
        id: 'governance-security',
        title: 'Power Platform Governance and Security',
        description: 'Establish governance frameworks and security policies',
        content: 'Governance implementation, security policies, and compliance management.',
        duration: '0.5 hours',
        difficulty: 'advanced'
      }
    ]
  },
  {
    id: 'power-automate-enterprise',
    title: 'Power Automate for Enterprise Solutions',
    description: 'Design and implement enterprise-scale automation solutions with Power Automate, including governance and security.',
    level: 'advanced',
    duration: '16 hours',
    category: 'power-automate',
    platform: 'Power Platform Enterprise',
    certificationPrep: true,
    prerequisites: ['power-platform-integration'],
    learningObjectives: [
      'Design enterprise-scale automation architectures',
      'Implement advanced security and compliance measures',
      'Establish center of excellence (CoE) practices',
      'Manage enterprise environments and ALM processes',
      'Optimize performance and scalability for large deployments'
    ],
    tags: ['Enterprise', 'Governance', 'Security', 'ALM', 'CoE', 'Scalability'],
    modules: [
      {
        id: 'enterprise-architecture',
        title: 'Enterprise Automation Architecture',
        description: 'Design scalable and maintainable enterprise automation architectures',
        content: `
# Enterprise Automation Architecture

## Architecture Patterns for Enterprise Automation

### Centralized vs. Distributed Architecture

**Centralized Architecture:**
- Single automation hub managing all flows and processes
- Centralized governance, monitoring, and control
- Easier to maintain consistency and enforce standards
- Best for: Organizations with strong central IT control

**Distributed Architecture:**
- Automation capabilities distributed across business units
- Department-level autonomy with central oversight
- Faster deployment within individual units
- Best for: Large organizations with diverse business needs

**Hybrid Architecture:**
- Combines centralized governance with distributed execution
- Central standards and policies, local implementation
- Most common in modern enterprises
- Balances control with agility

### Cloud-Native Architecture

**Multi-Environment Strategy:**
- Development, Test, UAT, Production environments
- Environment-specific configurations and connections
- Automated promotion pipelines between environments
- Environment parity for reliable deployments

**High Availability Design:**
- Redundant automation infrastructure
- Multi-region deployment strategies
- Failover and disaster recovery planning
- Business continuity considerations

### Scalability Design Principles

**Horizontal Scaling:**
- Add more automation instances as load increases
- Load balancing across multiple workers
- Queue-based processing for high-volume scenarios
- Elastic scaling based on demand

**Vertical Scaling:**
- Increase resources for individual automation instances
- Optimize flow performance and efficiency
- Resource allocation strategies
- Performance tuning and optimization

**Queue Management:**
- Centralized work queue architecture
- Priority-based processing
- Queue monitoring and alerting
- Retry and error handling strategies

## Automation Governance Frameworks

### Center of Excellence (CoE) Structure

**CoE Responsibilities:**
- Define automation standards and best practices
- Provide reusable templates and components
- Manage automation lifecycle governance
- Training and enablement programs
- Quality assurance and code reviews

**CoE Operating Model:**
- Centralized strategy and planning
- Distributed implementation teams
- Regular governance reviews
- Continuous improvement processes

### Solution Architecture Standards

**Naming Conventions:**
- Consistent naming for flows, connections, and variables
- Environment prefixes (DEV_, TEST_, PROD_)
- Functional area identifiers
- Version control indicators

**Modular Design Principles:**
- Break complex processes into smaller, reusable flows
- Child flow patterns for common operations
- Shared connection management
- Component library management

**Error Handling Standards:**
- Standardized error handling patterns
- Centralized error logging
- Automated error notification
- Retry logic and compensation handling

### Security Architecture

**Data Loss Prevention (DLP):**
- DLP policy design and implementation
- Connector classification (Business, Non-Business, Blocked)
- Data flow restrictions
- Compliance monitoring

**Access Control Framework:**
- Role-based access control (RBAC)
- Least privilege principles
- Environment-level security
- Connection sharing policies

**Secret Management:**
- Secure credential storage
- Key Vault integration
- Connection reference management
- Certificate and token handling

## Integration Architecture

### API-First Design

**API Strategy:**
- RESTful API design principles
- Custom connector development
- API versioning and lifecycle management
- API documentation standards

**Integration Patterns:**
- Request-response patterns
- Event-driven integration
- Batch processing patterns
- Real-time data synchronization

### Enterprise System Connectivity

**ERP Integration:**
- SAP, Oracle, Dynamics 365 connectors
- Data synchronization strategies
- Transaction integrity
- Performance optimization

**Legacy System Integration:**
- Custom connector development for legacy APIs
- Database direct connections
- File-based integration patterns
- Middleware and ESB integration

### Data Architecture

**Data Flow Design:**
- Source to destination mapping
- Data transformation strategies
- Data validation rules
- Master data management

**Data Storage:**
- Dataverse as central data repository
- SharePoint for document management
- SQL for transactional data
- Azure Storage for large datasets

## Performance Optimization

### Flow Performance Tuning

**Action Optimization:**
- Minimize HTTP calls within loops
- Use batch operations where possible
- Parallel branch execution
- Efficient filter and select operations

**Connection Efficiency:**
- Connection pooling and reuse
- Minimize connection changes
- Optimize authentication calls
- Use service principals for unattended flows

### Monitoring and Observability

**Telemetry Framework:**
- Application Insights integration
- Custom logging and metrics
- Performance counters
- Health monitoring dashboards

**Analytics and Reporting:**
- Usage analytics and trends
- Performance metrics tracking
- Cost analysis and optimization
- ROI measurement

## Deployment and ALM

### Application Lifecycle Management

**Solution Management:**
- Solution-based deployment
- Dependency management
- Version control integration
- Change management processes

**CI/CD Pipeline:**
- Automated build processes
- Automated testing frameworks
- Deployment automation
- Rollback procedures

**Environment Strategy:**
- Environment provisioning
- Configuration management
- Connection reference handling
- Environment variable management

### Change Management

**Release Planning:**
- Release cycles and cadence
- Impact assessment
- Stakeholder communication
- Rollout strategies

**Quality Assurance:**
- Unit testing frameworks
- Integration testing
- User acceptance testing
- Performance testing

## Cost Management

### License Optimization

**License Types:**
- Per-user licensing strategy
- Per-flow licensing for unattended scenarios
- Process and seeded license allocation
- License pooling strategies

**Usage Monitoring:**
- API call tracking
- Premium connector usage
- Run history analysis
- Cost allocation by department

### Resource Optimization

**Efficiency Measures:**
- Eliminate redundant flows
- Consolidate similar automations
- Optimize trigger conditions
- Reduce unnecessary executions

**Capacity Planning:**
- Forecast automation growth
- Plan infrastructure scaling
- Budget allocation
- ROI tracking and reporting

## Best Practices Summary

1. **Design for Scale:** Plan architecture to handle growth
2. **Standardize:** Use consistent patterns and templates
3. **Govern:** Implement strong governance from day one
4. **Monitor:** Track performance, usage, and costs
5. **Secure:** Build security into architecture, not as afterthought
6. **Document:** Maintain comprehensive documentation
7. **Train:** Invest in team capability development
8. **Iterate:** Continuously improve based on feedback
        `,
        duration: '4 hours',
        difficulty: 'advanced'
      },
      {
        id: 'security-compliance',
        title: 'Advanced Security and Compliance',
        description: 'Implement comprehensive security and compliance measures',
        content: 'Security frameworks, compliance management, and data protection strategies.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'coe-practices',
        title: 'Center of Excellence Implementation',
        description: 'Establish and manage a Power Platform Center of Excellence',
        content: 'CoE setup, governance policies, training programs, and community management.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'alm-processes',
        title: 'Application Lifecycle Management',
        description: 'Implement ALM processes for enterprise Power Platform solutions',
        content: 'ALM strategies, environment management, and deployment automation.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'performance-optimization',
        title: 'Performance Optimization and Monitoring',
        description: 'Optimize performance and implement comprehensive monitoring',
        content: 'Performance tuning, monitoring strategies, and capacity planning.',
        duration: '2 hours',
        difficulty: 'advanced'
      },
      {
        id: 'enterprise-deployment',
        title: 'Enterprise Deployment and Change Management',
        description: 'Manage large-scale deployments and organizational change',
        content: 'Deployment strategies, change management, and user adoption programs.',
        duration: '1.5 hours',
        difficulty: 'advanced'
      }
    ]
  }
];